import { Component, Input, OnInit, SimpleChanges, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import {
  CustomResultOfPaginatedListOfGetVoiceMessagesWithPaginationDto,
  GetEnumValuesDto,
  GetVoiceMessageByIdDto,
  GetVoiceMessageGroupsWithPaginationDto,
  GetVoiceMessagesWithPaginationDto,
  PaginatedListOfGetVoiceMessagesWithPaginationDto,
  VoiceStatus,
} from 'src/app/core/api/models';
import { VoiceMessagesService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { FormPageConfig } from 'src/app/core/models/form-page-config.model';
import { AppDomService } from 'src/app/core/services/dom.service';
import { BaseIndexPageComponent } from 'src/app/pages/shared/base/components/base-index-page.component';
import { UpdateGroupComponent } from './update-group/update-group.component';
import { UpdateStatusVoiceMessageComponent } from './update-status-voice-message/update-status-voice-message.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { VoiceMessagesFormComponent } from "./voice-messages-form/voice-messages-form.component";
import { AudioControllerModule } from "../../../shared/components/audio-controller/audio-controller.module";


@Component({
    selector: 'app-voice-messages',
    templateUrl: './voice-messages.component.html',
    imports: [BaseIndexModule, VoiceMessagesFormComponent, AudioControllerModule]
})
export class VoiceMessagesComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties
  @Input('group') input_group: GetVoiceMessageGroupsWithPaginationDto;

  pageName = 'voiceMessages';

  /* Binding service endpoints */
  protected currentService = inject(VoiceMessagesService);
  createGetApiMethodName: string =
    this.currentService.voiceMessagesUploadFileGet.name;
  auditApiMethodName: string =
    this.currentService.voiceMessagesGetVoiceMessageAuditWithPagination.name;

  modelWithPagination$: Observable<PaginatedListOfGetVoiceMessagesWithPaginationDto>;
  items$: Observable<Array<GetVoiceMessagesWithPaginationDto>>;
  formPageConfig: FormPageConfig<GetVoiceMessageByIdDto> = {};

  get VoiceStatus() {
    return VoiceStatus;
  }
  VoiceStatusEnumValues: GetEnumValuesDto[] = [];
  CreationLocationEnumValues: GetEnumValuesDto[] = [];

  //#endregion

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.pageConfig.showHelpBtn = false;
    // this.onCreate();
  }

  getEnumsValues() {
    this.getEnumValuesApi('VoiceStatus').subscribe(
      (values) => (this.VoiceStatusEnumValues = values)
    );
    this.getEnumValuesApi('EntityCreationLocation')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.CreationLocationEnumValues = values));
  }

  configTableColumns(): void {
    this.tableColumns = [
      {
        title: ``,
        name: '',
        hasSearch: false,
        noSorting: true,
        isCheckBox: true,
        wrap: true,
      },
      {
        title: this.localizer.id,
        name: 'id',
        hasSearch: true,
        type: 'number',
        noNumberSeparator: true,
      },
      {
        title: this.localizer.name,
        name: 'name',
        hasSearch: true,
        type: 'text',
      },
      {
        title: this.localizer.organization,
        name: 'organizationName',
        hasSearch: true,
      },
      {
        title: this.localizer.status,
        name: 'status',
        hasSearch: true,
        type: 'enum',
        enumName: 'VoiceStatus',
      },
      {
        title: 'common.creationLocation',
        name: 'creationLocation',
        hasSearch: true,
        type: 'enum',
        enumName: 'EntityCreationLocation',
        wrap: true,
      },
      {
        title: 'common.voiceFile',
        name: 'path',
        hasSearch: false,
        noSorting: true,
        type: 'text',
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
      },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['input_group']) {
        this.getTableData();
      }
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  getTableData() {
    try {
      if (!this.input_group) return;
      super.getTableData();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  //#region api

  getPaginationApi(): Observable<CustomResultOfPaginatedListOfGetVoiceMessagesWithPaginationDto> {
    return this.currentService.voiceMessagesGetVoiceMessageWithPagination({
      body: {
        ...this.getBaseModelWithPaginationParams(),
        voiceMessageGroupId: this.input_group.id,
      },
    });
  }

  //Delete
  baseDeleteApi(id: number) {
    this.unSubs();

    this.pageConfig.loading = true;
    this.lastSelectedItemId = id;

    this.currentService
      .voiceMessagesDeleteDelete({ body: { id: id } })
      // .pipe(this.basePipeOperators())
      .subscribe(this.baseDeleteObserver);
  }

  //Get ById
  baseGetByIdApi(model: GetVoiceMessagesWithPaginationDto): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = model.id;

    this.currentService
      .voiceMessagesUpdateStatusGet({ id: model.id })
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        this.openEditSendSmsDialog(model);
      });
  }

  getNewStatusGetApi(id: number, name: string): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.currentService
      .voiceMessagesGetNewStatusGet({})
      .pipe(this.basePipeOperators())
      .subscribe((res) => {
        if (res) this.getNewStatusConfirmation(id, name);
      });
  }

  getNewStatusPostApi(id: number): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.currentService
      .voiceMessagesGetNewStatusPost({ body: { id: id } })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  deleteVoicesListApi(ids: number[]) {
    this.unSubs();

    this.pageConfig.loading = true;
    this.lastSelectedItemId = ids;

    this.currentService
      .voiceMessagesDeleteVoiceMessagesByIdsDelete({ body: { ids: ids } })
      .pipe(
        this.basePipeOperators(),
        finalize(() => (this.selectedIds = []))
      )
      .subscribe(this.baseGroupDeleteObserver);
  }
  //#endregion

  onEditStatus(model: GetVoiceMessagesWithPaginationDto) {
    this.baseGetByIdApi(model);
  }

  openEditSendSmsDialog(model: GetVoiceMessagesWithPaginationDto) {
    // GetVoiceMessagesWithPaginationDto
    // UpdateStatusCommand
    this.unSubs();
    //TODO change voice type
    let data: any = {
      id: model.id,
      status: model.status,
    };

    const modalResult = this.dialogService.openModal({
      component: UpdateStatusVoiceMessageComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  onGetNewStatus(id, name) {
    this.unSubs();

    this.pageConfig.loading = true;

    this.getNewStatusGetApi(id, name);
  }

  getNewStatusConfirmation(id: number, name: string) {
    const dialogResult = this.getConfirmDialog({
      title: 'messages.globalConfirmationMessage',
      content: `${id.toString()} (${name})`,
    });

    dialogResult
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.getNewStatusPostApi(id);
        }
      });
  }

  onUpdateGroup() {
    try {
      this.unSubs();

      let data = {
        selectedVoices: this.selectedIds,
        groupId: this.input_group.id,
      };

      const modalResult = this.dialogService.openModal({
        component: UpdateGroupComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.selectedIds = [];
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  callGroupDeletion() {
    this.deleteVoicesListApi(this.selectedIds);
  }
}
